import Bold from '../atoms/text/bold';
import Channel from '../../../shared/classes/channel';
import ChannelList from '../sections/list/channelList/channelList';
import ChannelListContentContainer from '@components/atoms/container/channelListContentContainer';
import ChannelUtils from '../../../shared/utils/channels';
import Content from '../../../shared/types/enums/content';
import DefaultButton from '../elements/buttons/defaultButton';
import Focus from '../../../shared/types/enums/focus';
import List from '../sections/list/list';
import MainContainer from '../atoms/container/mainContainer';
import ManageChannelContext from '../../../shared/components/utils/context/manageChannelsContext';
import ManageChannelsButtonsContainer from '../atoms/container/manageChannelsButtonContainer';
import ManageChannelsInfoContainer from '../atoms/container/manageChannelsInfoContainer';
import ManageChannelsText from '../atoms/text/manageChannelsText';
import Storage from '../../../shared/utils/storage';
import TranslationManager from '../../../shared/assets/locale/translationsManager';
import closeMenu from '../../../shared/actions/menu/closeMenu';
import putChannelsOrder from '../../../shared/api/channels/putChannelsOrder';
import useBackKeyboard from '../../../shared/components/utils/hooks/useBackKeyboard';
import useKeyboard from '../../../shared/components/utils/hooks/useKeyboard';
import useMenu from '../../../shared/components/utils/hooks/useMenu';
import { IChannelOrder } from '../../../shared/types/interfaces/channel';
import { initChannels } from '../../../shared/actions/channels/initChannels';
import { loadChannels as loadVisibleChannels } from '../../../shared/store/thunks/channels.thunk';
import { popRoute } from '../../../shared/store/thunks/route.thunk';
import { store } from '../../../shared/store/store';
import { useAppDispatch, useAppSelector } from '../../../shared/store/hooks';
import { useEffect, useRef, useState } from 'react';
import {
  channelsSelector,
  loadHiddenChannels,
} from '../../../shared/store/slices/channels.slice';
import {
  setFocus,
  focusSelector,
} from '../../../shared/store/slices/focus.slice';

const LITERALS = 'PAGES.SETTINGS.MANAGE_CHANNELS';

function TManageChannels() {
  const dispatch = useAppDispatch();
  const { channelsData, hiddenChannels } = useAppSelector(channelsSelector);
  const { focus } = useAppSelector(focusSelector);
  const [editing, setEditing] = useState(false);

  const allChannels = ChannelUtils.getAllChannels(true, true);
  const menuFocused = focus === Focus.MENU;

  const allChannelsDataRef = useRef(allChannels);
  const currentChannelRef = useRef<Channel>();
  const editingRef = useRef<boolean>(editing);
  const focusRef = useRef(focus);
  const originalChannels = useRef(allChannels);

  editingRef.current = editing;
  focusRef.current = focus;

  // Actions

  const returnAction = () => {
    dispatch(popRoute());
  };

  // Event handlers

  const onChange = (
    channel: Channel | undefined,
    increment: number | undefined
  ) => {
    if (editingRef.current) {
      if (!currentChannelRef?.current) {
        currentChannelRef.current = allChannelsDataRef.current?.length
          ? allChannelsDataRef.current[0]
          : undefined;
      }
      const itemIdx = allChannelsDataRef.current?.findIndex(
        (item) => item.id === currentChannelRef?.current?.id
      );
      if (itemIdx !== -1) {
        const items = [...(allChannelsDataRef.current as Channel[])];
        items.splice(itemIdx as number, 1);
        items.splice(
          (itemIdx as number) + (increment || 0),
          0,
          currentChannelRef?.current as Channel
        );
        items.forEach((item, index) => {
          item.dial = index + 1;
        });
        allChannelsDataRef.current = items;
        const hiddenChannels = items.filter((item) => item.hidden);
        const visibleChannels = items.filter(
          (item) => !item.hidden && item.type === Content.CHANNEL
        );
        dispatch(loadHiddenChannels(hiddenChannels));
        dispatch(loadVisibleChannels(visibleChannels));
      }
    } else {
      currentChannelRef.current = channel;
    }
  };

  // Menu hook

  const outOfBounds = useMenu(null);

  // Keyboard hooks

  useBackKeyboard();

  useKeyboard({
    focus: Focus.CHANNEL_LIST,
    keyDownHandler: (key) => {
      const { focus: focusSlice } = store.getState();
      if (key === 'SELECT' && !editingRef.current) {
        setEditing(true);
      } else if (key === 'SELECT' && editingRef.current) {
        setEditing(false);
      }

      if (key === 'RIGHT' && focusSlice.focus === Focus.CHANNEL_LIST) {
        dispatch(setFocus(Focus.MANAGE_CHANNELS_BUTTONS));
      }
    },
  });

  // Effect hooks

  useEffect(() => {
    dispatch(setFocus(Focus.CHANNEL_LIST));
    return () => {
      setEditing(false);
      const hiddenChannels = originalChannels.current.filter(
        (item) => item.hidden
      );
      const visibleChannels = originalChannels.current.filter(
        (item) => !item.hidden && item.type === Content.CHANNEL
      );
      dispatch(loadHiddenChannels(hiddenChannels));
      dispatch(loadVisibleChannels(visibleChannels));
      ![Focus.MENU, Focus.LOGIN].includes(focusRef.current as number) &&
        dispatch(setFocus(Focus.MOSAIC));
    };
  }, []);

  useEffect(() => {
    if (channelsData?.length && !originalChannels.current.length) {
      originalChannels.current = channelsData;
    }
  }, [channelsData, hiddenChannels]);

  return (
    <ManageChannelContext.Provider value={{ editing, setEditing }}>
      <MainContainer id={'manageChannelsContainer'}>
        <ChannelListContentContainer
          animation={'channel-list-content'}
          focused={!menuFocused}
        >
          {allChannels ? (
            <ChannelList
              onChangeState={onChange}
              preventAction={true}
              channelsData={allChannels}
              currentIndex={0}
              outOfBounds={(focus: number | string, key?: string) => {
                outOfBounds(key);
              }}
              pop={false}
            />
          ) : (
            <></>
          )}
        </ChannelListContentContainer>
        <ManageChannelsInfoContainer>
          <ManageChannelsText>
            <Bold>{TranslationManager.get(`${LITERALS}.ORDER`)}</Bold>
            <br></br>
            {TranslationManager.get(`${LITERALS}.PRESS_OK`)}
            <br></br>
            {TranslationManager.get(`${LITERALS}.MOVE`)}
            <br></br>
            {TranslationManager.get(`${LITERALS}.PRESS_OK_TO_FIX`)}
            <br></br>
            {TranslationManager.get(`${LITERALS}.FINISH`)}
          </ManageChannelsText>
          <ManageChannelsButtonsContainer
            onMouseEnter={() => {
              closeMenu();
              dispatch(setFocus(Focus.MANAGE_CHANNELS_BUTTONS));
            }}
          >
            <List
              focus={Focus.MANAGE_CHANNELS_BUTTONS}
              onChange={() => null}
              outOfBounds={(focus, key) => {
                if (key === 'LEFT') {
                  dispatch(setFocus(Focus.CHANNEL_LIST));
                }
              }}
            >
              <DefaultButton
                index={0}
                text={`${LITERALS}.BUTTONS.ACCEPT`}
                action={() => {
                  originalChannels.current = allChannels;
                  allChannels.filter((item) => !item.hidden);
                  const storageChannels = allChannels.map((item, index) => {
                    return { id: item?.id, order: index };
                  });
                  const orderedChannels = allChannels.map((item) => {
                    return {
                      channelCode: item.code,
                      dial: item.dial,
                      hidden: item.hidden,
                    } as IChannelOrder;
                  });
                  Storage.set('channelOrder', JSON.stringify(storageChannels));
                  putChannelsOrder(orderedChannels);
                  returnAction();
                }}
              />
              <DefaultButton
                index={1}
                text={`${LITERALS}.BUTTONS.RESTORE`}
                action={() => {
                  originalChannels.current = [];
                  Storage.delete('channelOrder');
                  initChannels(true);
                  returnAction();
                }}
              />
              <DefaultButton
                index={2}
                text={`${LITERALS}.BUTTONS.CANCEL`}
                action={returnAction}
              />
            </List>
          </ManageChannelsButtonsContainer>
        </ManageChannelsInfoContainer>
      </MainContainer>
    </ManageChannelContext.Provider>
  );
}

export default TManageChannels;
